/* RelationshipSelector — shared relationship dropdown used by Admin, Family,
   and memorial-creation wizard forms. Renders a <select> with a curated list
   of presets plus an "Other" option that reveals a free-text input for a
   custom relationship label. Validation of the custom value (when "other" is
   selected) is the caller's responsibility — see `resolveRelationship` below. */
import React from 'react';

const PRESETS = {
  // Used by "Who carries the keys" / admin invites — immediate family only.
  admin: [
    { value: 'son', label: 'Son' },
    { value: 'daughter', label: 'Daughter' },
    { value: 'father', label: 'Father' },
    { value: 'mother', label: 'Mother' },
    { value: 'wife', label: 'Wife' },
    { value: 'husband', label: 'Husband' },
    { value: 'spouse', label: 'Spouse' },
    { value: 'brother', label: 'Brother' },
    { value: 'sister', label: 'Sister' },
  ],
  // Used by "People closest to them" — immediate family.
  immediate: [
    { value: 'son', label: 'Son' },
    { value: 'daughter', label: 'Daughter' },
    { value: 'father', label: 'Father' },
    { value: 'mother', label: 'Mother' },
    { value: 'brother', label: 'Brother' },
    { value: 'sister', label: 'Sister' },
  ],
  // Used in the Family tab — broader list incl. extended relatives & friends.
  family: [
    { value: 'spouse', label: 'Spouse' },
    { value: 'partner', label: 'Partner' },
    { value: 'wife', label: 'Wife' },
    { value: 'husband', label: 'Husband' },
    { value: 'son', label: 'Son' },
    { value: 'daughter', label: 'Daughter' },
    { value: 'father', label: 'Father' },
    { value: 'mother', label: 'Mother' },
    { value: 'brother', label: 'Brother' },
    { value: 'sister', label: 'Sister' },
    { value: 'grandfather', label: 'Grandfather' },
    { value: 'grandmother', label: 'Grandmother' },
    { value: 'grandson', label: 'Grandson' },
    { value: 'granddaughter', label: 'Granddaughter' },
    { value: 'uncle', label: 'Uncle' },
    { value: 'aunt', label: 'Aunt' },
    { value: 'cousin', label: 'Cousin' },
    { value: 'nephew', label: 'Nephew' },
    { value: 'niece', label: 'Niece' },
    { value: 'in-law', label: 'In-law' },
    { value: 'friend', label: 'Friend' },
  ],
};

export const RELATIONSHIP_PRESETS = PRESETS;

export function RelationshipSelector({
  value,
  customValue = '',
  onChange,
  preset = 'family',
  selectTestId,
  inputTestId,
  selectClassName = 'ml-input',
  inputClassName = 'ml-input mt-2',
  placeholder = 'Select…',
  customPlaceholder = 'Type relationship',
}) {
  const options = PRESETS[preset] || PRESETS.family;
  const isOther = value === 'other';
  return (
    <>
      <select
        className={selectClassName}
        value={value || ''}
        onChange={(e) => {
          const v = e.target.value;
          // When the user switches away from "other", clear any custom text.
          onChange(v, v === 'other' ? customValue : '');
        }}
        data-testid={selectTestId}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
        <option value="other">Other</option>
      </select>
      {isOther && (
        <input
          type="text"
          className={inputClassName}
          placeholder={customPlaceholder}
          value={customValue || ''}
          onChange={(e) => onChange('other', e.target.value)}
          aria-label="Relationship"
          maxLength={60}
          data-testid={inputTestId}
        />
      )}
    </>
  );
}

/** Returns the relationship string to send to the backend. When the user
 *  picked "Other", the custom text replaces the value so existing storage,
 *  filtering, and display logic keeps working without any backend change. */
export function resolveRelationship(value, customValue) {
  if (value === 'other') return (customValue || '').trim();
  return value || '';
}

/** True when the form should refuse to submit because the user picked
 *  "Other" but didn't type any custom relationship. */
export function isRelationshipMissing(value, customValue) {
  if (!value) return true;
  if (value === 'other' && !(customValue || '').trim()) return true;
  return false;
}
